import { Button } from "../../../../core/Button.js";
import { Panel } from "../../../../core/Panel.js";
import { Skeleton } from "../../../../core/Skeleton.js";
import { Table, type TableColumn } from "../../../../core/Table.js";
import { Text } from "../../../../core/Text.js";
import { Amount } from "../../../../domain/Amount.js";
import type { AccountShippingContent, ShippingDestinationRow } from "../../accountContent.js";

/**
 * Settings → Shipping: the saved address with Add or Edit, then where the store ships, each
 * country with its cost and wait, the saved address's country marked.
 */
export function AccountShipping({
  title,
  description,
  currency,
  address,
  edit,
  destinations,
}: AccountShippingContent) {
  return (
    <Panel title={title} description={description}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        {address.status === "loading" && (
          <div aria-busy className="flex w-48 flex-col gap-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        )}
        {address.status === "none" && (
          <Text variant="body-b1" tone="secondary">
            {address.message}
          </Text>
        )}
        {address.status === "saved" && (
          <address className="flex flex-col gap-0.5 font-body text-body-b1 text-text-primary not-italic">
            {address.lines.map((line) => (
              <span key={line}>{line}</span>
            ))}
          </address>
        )}
        {edit && (
          <Button
            variant={edit.emphasis === "primary" ? "primary" : "secondary"}
            size="sm"
            disabled={edit.disabled}
            onClick={edit.onClick}
          >
            {edit.label}
          </Button>
        )}
      </div>
      {destinations.status === "unavailable" ? (
        <Text variant="body-b2" tone="secondary">
          {destinations.message}
        </Text>
      ) : (
        <Table
          caption={destinations.title}
          density="compact"
          rows={destinations.rows}
          rowKey={(row) => row.code}
          columns={destinationColumns(destinations, currency)}
        />
      )}
    </Panel>
  );
}

function destinationColumns(
  destinations: Extract<AccountShippingContent["destinations"], { status: "ready" }>,
  currency: AccountShippingContent["currency"],
): TableColumn<ShippingDestinationRow>[] {
  return [
    {
      key: "country",
      header: destinations.columns.country,
      rowHeader: true,
      cell: (row) =>
        row.current ? (
          <span className="font-body text-body-b2-strong text-text-primary">{row.country}</span>
        ) : (
          row.country
        ),
    },
    {
      key: "cost",
      header: destinations.columns.cost,
      numeric: true,
      nowrap: true,
      cell: (row) =>
        row.costCents === 0 ? (
          destinations.freeLabel
        ) : (
          <Amount cents={row.costCents} currency={currency} size="meta" />
        ),
    },
    { key: "delivery", header: destinations.columns.delivery, cell: (row) => row.delivery },
  ];
}
